import { PublicRoutes } from "@/constants/routes";
import {
  Headset,
  MessageCircle,
  Newspaper,
  ShieldCheck,
} from "lucide-react";
import { CHANNELS, COMPANY, DPO, OMBUDSMAN } from "./company";
import type { LegalDocument } from "./types";

/** `5519936195831` → "+55 (19) 93619-5831". */
const formatPhone = (digits: string) =>
  `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;

const COMMERCIAL_WA = `https://wa.me/${CHANNELS.commercialWhatsapp}?text=${encodeURIComponent(
  "Olá! Quero conhecer a Fidexa para a cobrança do crediário da minha loja."
)}`;

const SUPPORT_WA = `https://wa.me/${CHANNELS.supportWhatsapp}?text=${encodeURIComponent(
  "Olá! Já sou cliente Fidexa e preciso de ajuda. Loja: "
)}`;

// Fatos: horário e prazo do suporte vêm do contrato (mesma fonte de
// /disponibilidade); e-mail de imprensa, razão social, CNPJ e endereço estão em
// .claude/skills/seo/reference/fidexa-empresa.md e em company.ts.
export const CONTACT: LegalDocument = {
  key: "contato",
  path: PublicRoutes.CONTATO,
  eyebrow: "Atendimento",
  title: "Fale com a Fidexa",
  seoTitle: "Contato: WhatsApp, e-mail e endereço",
  description:
    "Canais de contato da Fidexa: WhatsApp comercial para conhecer a plataforma, suporte para quem já é cliente, imprensa, ouvidoria, dados pessoais e endereço.",
  intro:
    "Cada assunto tem um canal próprio, para a resposta chegar rápido e de quem resolve. Escolha o seu abaixo.",
  version: "1.0",
  updatedAt: "2026-10-15",

  highlights: [
    {
      icon: MessageCircle,
      title: "Quero conhecer a Fidexa",
      text: `Fale com o time comercial pelo WhatsApp ${formatPhone(CHANNELS.commercialWhatsapp)} ou agende uma demonstração gratuita.`,
    },
    {
      icon: Headset,
      title: "Já sou cliente",
      text: `Suporte pelo WhatsApp ${formatPhone(CHANNELS.supportWhatsapp)}, de segunda a sexta, das 9h às 18h, com primeira resposta em até 24 horas úteis.`,
    },
    {
      icon: Newspaper,
      title: "Imprensa",
      text: `Pautas, entrevistas e materiais de marca: escreva para ${CHANNELS.pressEmail}.`,
    },
    {
      icon: ShieldCheck,
      title: "Dados pessoais e ouvidoria",
      text: `Pedidos de titulares vão para ${DPO.name}, encarregado de dados. Reclamações não resolvidas, para a Ouvidoria.`,
    },
  ],

  sections: [
    {
      id: "comercial",
      title: "Comercial: conhecer a plataforma",
      summary:
        "Para quem vende no crediário próprio e quer ver como a Fidexa cobra pelo WhatsApp. Atendimento por WhatsApp ou demonstração agendada.",
      blocks: [
        {
          type: "paragraph",
          text: "Conte quantas lojas você tem e qual sistema de gestão usa. Com isso o time comercial já consegue mostrar a plataforma com o cenário da sua rede e estimar quanto do crediário em atraso dá para recuperar.",
        },
        {
          type: "action",
          label: `WhatsApp comercial ${formatPhone(CHANNELS.commercialWhatsapp)}`,
          href: COMMERCIAL_WA,
        },
        {
          type: "paragraph",
          text: 'Prefere agendar? Use o botão "Fale com um especialista" no topo desta página e escolha o melhor horário.',
        },
      ],
    },
    {
      id: "suporte",
      title: "Suporte: já sou cliente",
      summary:
        "Segunda a sexta, das 9h às 18h (horário de Brasília), com primeira resposta em até 24 horas úteis.",
      blocks: [
        {
          type: "table",
          caption: "Como funciona o suporte",
          columns: [
            { key: "item", header: "Item", primary: true },
            { key: "como", header: "Como funciona" },
          ],
          rows: [
            {
              item: "Canal",
              como: `WhatsApp ${formatPhone(CHANNELS.supportWhatsapp)}`,
            },
            {
              item: "Horário",
              como: "Segunda a sexta-feira, das 9h às 18h (horário de Brasília), exceto feriados.",
            },
            {
              item: "Primeira resposta",
              como: "Em até 24 horas úteis após a abertura do chamado.",
            },
          ],
        },
        {
          type: "action",
          label: "Abrir chamado no WhatsApp",
          href: SUPPORT_WA,
        },
        {
          type: "paragraph",
          text: `Para resolver mais rápido, conte o que aconteceu, em qual loja e, se possível, envie um print da tela. O nível de serviço completo está em [Disponibilidade e suporte](${PublicRoutes.DISPONIBILIDADE}).`,
        },
      ],
    },
    {
      id: "imprensa",
      title: "Imprensa",
      summary:
        "Pautas, entrevistas com os fundadores e materiais de marca pelo e-mail de imprensa.",
      blocks: [
        {
          type: "paragraph",
          text: `Matérias já publicadas, release e logos estão na [Newsroom](${PublicRoutes.NEWSROOM}). Para entrevista ou dado novo, escreva para o e-mail abaixo com o veículo, o tema e o prazo.`,
        },
        {
          type: "action",
          label: CHANNELS.pressEmail,
          href: `mailto:${CHANNELS.pressEmail}?subject=${encodeURIComponent("Imprensa - contato com a Fidexa")}`,
        },
      ],
    },
    {
      id: "dados-pessoais",
      title: "Dados pessoais (LGPD)",
      summary: `Pedidos de acesso, correção ou exclusão de dados vão para ${DPO.name}, encarregado de dados, e são respondidos em até 15 dias.`,
      blocks: [
        {
          type: "paragraph",
          text: `Se você recebeu uma cobrança pelo WhatsApp e quer saber de onde vieram seus dados, o pedido também vale: a Fidexa avisa a loja que cadastrou os dados e acompanha a resposta. Como funciona está em [LGPD na Fidexa](${PublicRoutes.LGPD}).`,
        },
        {
          type: "action",
          label: DPO.email,
          href: `mailto:${DPO.email}?subject=${encodeURIComponent("Dados pessoais - pedido de titular")}`,
        },
      ],
    },
    {
      id: "ouvidoria",
      title: "Ouvidoria",
      summary: `Quando o atendimento normal não resolveu: ${OMBUDSMAN.name}, sócio que não atua na operação, avalia o caso.`,
      blocks: [
        {
          type: "paragraph",
          text: `A Ouvidoria recebe reclamações não resolvidas, denúncias e sugestões, por escrito. Quando usar e o que informar está em [Ouvidoria](${PublicRoutes.OUVIDORIA}).`,
        },
        {
          type: "action",
          label: OMBUDSMAN.email,
          href: `mailto:${OMBUDSMAN.email}?subject=${encodeURIComponent("Ouvidoria Fidexa - manifestação formal")}`,
        },
      ],
    },
    {
      id: "endereco",
      title: "Endereço e dados da empresa",
      summary: "Razão social, CNPJ e endereço para correspondência.",
      blocks: [
        {
          type: "table",
          caption: "Dados institucionais",
          columns: [
            { key: "item", header: "Item", primary: true },
            { key: "valor", header: "Valor" },
          ],
          rows: [
            { item: "Razão social", valor: COMPANY.legalName },
            { item: "CNPJ", valor: COMPANY.cnpj },
            { item: "Endereço", valor: COMPANY.address },
          ],
        },
        {
          type: "callout",
          tone: "info",
          title: "Atendimento é remoto",
          text: "A Fidexa atende lojas de todo o Brasil pelo WhatsApp e por videochamada. O endereço é para correspondência, não para atendimento presencial.",
        },
      ],
    },
  ],

  changelog: [
    {
      version: "1.0",
      date: "2026-10-15",
      changes: ["Primeira versão publicada."],
    },
  ],
};
